import { ProfileOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Input, Space, Table, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDateTime, formatMileage } from "../../shared/utils/formatters";
import { customerName } from "../customers/customer-types";
import { vehiclesApi } from "./vehicles-api";
import { useTimedNoResults } from "../../shared/hooks/useTimedNoResults";

export function VehicleListPage() {
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const vehicles = useQuery({ queryKey: ["vehicles", query, page], queryFn: () => vehiclesApi.list(query, undefined, page) });
  const noSearchResultsDismissed = useTimedNoResults({
    query,
    enabled: Boolean(query.trim()),
    loading: vehicles.isFetching,
    ready: Boolean(vehicles.data),
    hasResults: Boolean(vehicles.data?.items.length)
  });
  return <section>
    <div className="page-title business-page-title">
      <div><Typography.Title level={2}>{text.vehicles}</Typography.Title><Typography.Paragraph type="secondary">{text.vehicleListHint}</Typography.Paragraph></div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/vehicles/new")}>{text.newVehicle}</Button>
    </div>
    <Card>
      <Space.Compact className="list-search">
        <Input value={search} allowClear prefix={<SearchOutlined />} placeholder={`${text.search} VIN, plate…`} onChange={event => setSearch(event.target.value.toUpperCase())} onPressEnter={() => { setPage(1); setQuery(search); }} />
        <Button onClick={() => { setPage(1); setQuery(search); }}>{text.search}</Button>
      </Space.Compact>
      <Table
        rowKey="id"
        loading={vehicles.isLoading}
        dataSource={vehicles.data?.items ?? []}
        locale={{ emptyText: query && noSearchResultsDismissed ? null : text.noSearchResults }}
        rowClassName="clickable-row"
        onRow={row => ({ onClick: () => navigate(`/vehicles/${row.id}`) })}
        scroll={{ x: 900 }}
        pagination={{ current: page, pageSize: 20, total: vehicles.data?.total ?? 0, showSizeChanger: false, onChange: setPage }}
        columns={[
          { title: text.vehicle, key: "vehicle", render: (_, row) => <div><b>{row.year} {row.make} {row.model}</b><small className="table-secondary">{row.color || "—"}</small></div> },
          { title: text.vin, dataIndex: "vin", key: "vin", className: "mono" },
          { title: text.plate, dataIndex: "licensePlate", key: "plate", render: value => value || "—" },
          { title: text.owner, key: "owner", render: (_, row) => customerName(row.customer) },
          { title: text.mileage, dataIndex: "mileage", key: "mileage", align: "right" as const, render: formatMileage },
          { title: text.lastVisitAt, dataIndex: "lastVisitAt", key: "lastVisitAt", render: value => value ? formatDateTime(value) : "—" },
          { title: "", key: "actions", width: 150, render: (_, row) => <Button
            icon={<ProfileOutlined />}
            onClick={event => { event.stopPropagation(); navigate(`/vehicles/${row.id}/specifications`); }}
          >{text.detailedConfiguration}</Button> }
        ]}
      />
    </Card>
  </section>;
}
